import type { BatteryBankCurrents } from "../battery-bank";
import { getDefinition } from "../catalog";
import { reserveSocForChemistry } from "../battery-reserve";
import { computeEnergySnapshot } from "../diagnostics/energy";
import { batteryCurrentsFromEnergyFlow } from "../diagnostics/connected-runtime";
import { computeEnergizedTerminals } from "../diagnostics/graph";
import type { ComponentNode, System } from "../types";
import type { SimulateContext } from "../cable-current";

export { DEFAULT_SOC } from "../battery-reserve";

const EMPTY_LOAD_STATES = {};
const currentsCache = new WeakMap<System, WeakMap<object, WeakMap<object, Map<ReadonlySet<string> | undefined, Map<string, number>>>>>();

/** Shared per-state ledger. Component cards build separate context wrappers,
 * but their component/load state objects are shared, so one circuit evaluation
 * supplies every battery card in the drawing. */
function runtimeCurrents(system: System, simulate: SimulateContext): Map<string, number> {
  let byStates = currentsCache.get(system);
  if (!byStates) { byStates = new WeakMap(); currentsCache.set(system, byStates); }
  let byLoads = byStates.get(simulate.componentStates);
  if (!byLoads) { byLoads = new WeakMap(); byStates.set(simulate.componentStates, byLoads); }
  const loads = simulate.loadStates ?? EMPTY_LOAD_STATES;
  let byEnergized = byLoads.get(loads);
  if (!byEnergized) { byEnergized = new Map(); byLoads.set(loads, byEnergized); }
  const known = byEnergized.get(simulate.energizedTerminals);
  if (known) return known;
  const energized = simulate.energizedTerminals ?? computeEnergizedTerminals(system, getDefinition, simulate);
  const { flow } = computeEnergySnapshot(system, getDefinition, simulate.componentStates, {}, loads, energized);
  const currents = batteryCurrentsFromEnergyFlow(system, getDefinition, flow);
  byEnergized.set(simulate.energizedTerminals, currents);
  return currents;
}

/** Positive is charging, negative is discharging. Series cells carry the same
 * string current; parallel members share by capacity. The optional legacy bank
 * argument remains source-compatible, but sizing bounds never replace live
 * power flow. */
export function netCurrentAtBattery(
  battery: ComponentNode,
  system: System,
  simulate: SimulateContext,
  _bank?: BatteryBankCurrents,
): number {
  return runtimeCurrents(system, simulate).get(battery.id) ?? 0;
}

export type BatteryRuntime = {
  /** 0 when the battery declares no `capacityAh`. */
  capacityAh: number;
  /** Fraction in [0, 1]. */
  soc: number;
  /** + charging, − discharging, 0 idle. */
  netCurrentA: number;
  /** Hours to 100%. `Infinity` when idle or discharging. */
  timeToFullHours: number;
  /** Hours to the reserve floor. `Infinity` when idle or charging. */
  timeToEmptyHours: number;
};

export function batteryRuntime(
  battery: ComponentNode,
  system: System,
  simulate: SimulateContext,
  soc: number,
  bank?: BatteryBankCurrents,
): BatteryRuntime {
  const def = getDefinition(battery.definitionId, battery.customDefinition);
  const capacityAh = def?.capacityAh ?? 0;
  const netCurrentA = netCurrentAtBattery(battery, system, simulate, bank);

  let timeToFullHours = Infinity;
  let timeToEmptyHours = Infinity;
  if (capacityAh > 0) {
    if (netCurrentA > 0 && soc < 1) {
      timeToFullHours = ((1 - soc) * capacityAh) / netCurrentA;
    }
    if (netCurrentA < 0) {
      const availableAh = Math.max(0, soc - reserveSocForChemistry(def?.chemistry)) * capacityAh;
      timeToEmptyHours = availableAh / -netCurrentA;
    }
  }
  return { capacityAh, soc, netCurrentA, timeToFullHours, timeToEmptyHours };
}

export function formatHours(hours: number): string {
  if (!isFinite(hours)) return "—";
  if (hours < 1) return `${Math.round(hours * 60)} min`;
  if (hours < 10) return `${hours.toFixed(1)} h`;
  if (hours < 48) return `${Math.round(hours)} h`;
  const days = hours / 24;
  if (days < 10) return `${days.toFixed(1)} d`;
  return `${Math.round(days)} d`;
}
